/**
 * @module Return Visitor Cookie
 * @description Utilities for managing the x-cohort-return-visitor cookie
 */
import { getCookie, setCookie, deleteCookie } from "@/js/utils/cookie";

/**
 * @interface ReturnVisitorCookie
 * @property {string} env - Environment (production, staging, dev)
 * @property {string} controlNumber - Address ID (FTR-sourced, returned after serviceability)
 * @property {string} sessionId - uuid v4 per session
 * @property {boolean} shownReturnVisitorOffer - Whether user has been shown the Return Visitor Offer popup
 */

const COOKIE_NAME = "x-cohort-return-visitor";
const COOKIE_EXPIRES_DAYS = 90;
const COOKIE_EXPIRES_MINUTES = COOKIE_EXPIRES_DAYS * 24 * 60; // 90 days in minutes

/**
 * Check if the current environment is localhost
 * @returns {boolean}
 */
const isLocalhost = () => {
  if (typeof window === "undefined") return false;
  const { hostname } = window.location;
  return hostname === "localhost" || hostname === "127.0.0.1";
};

/**
 * TEMPORARY: Set to true to bypass OneTrust consent check, currently there is no way to remove c0002 consent group
 * so having this flag would just bypass unneeded logic
 * TODO: Set to false once we have implemented a reliable way to wait for onetrust to load
 */
const BYPASS_ONETRUST_CONSENT_CHECK = true;

/**
 * Check if marketing consent is granted via OneTrust
 * @returns {boolean}
 */
export const hasMarketingConsent = () => {
  // TEMPORARY: Bypass consent check for testing
  if (BYPASS_ONETRUST_CONSENT_CHECK) {
    return true;
  }

  if (typeof window === "undefined") return false;

  try {
    // OneTrust consent groups - typically C0002 is for marketing/targeting cookies
    // You may need to adjust this group ID based on your OneTrust configuration
    const marketingGroupId = "C0002";

    // Use window.OnetrustActiveGroups, which is a comma-separated list of the active categories.
    // If "C0002" (marketingGroupId) is included, we have consent.
    // See: https://onetrust-pub.site.com/s/article/UUID/How-does-OnetrustActiveGroups-work
    const onetrustActiveGroups = window?.OnetrustActiveGroups;

    if (onetrustActiveGroups) {
      const hasConsent = onetrustActiveGroups.includes(marketingGroupId);

      // Override for testing in dev/localhost
      const localhost = isLocalhost();
      if (!hasConsent && localhost) {
        return true;
      }

      return hasConsent;
    }

    return false;
  } catch (error) {
    console.error("Error checking marketing consent:", error);
    // Default to false for privacy compliance
    return false;
  }
};
/**
 * Build cookie data object with defaults
 * @param {Object} existingData - Existing cookie data
 * @param {Object} newData - New data to merge
 * @returns {Object} - Complete cookie data object
 */
const buildCookieData = (existingData = {}, newData = {}) => {
  // Get current Cohesion session ID, fallback to uuid if not available
  const cohesionSessionId =
    typeof window !== "undefined" && window._Cohesion?.sessionId
      ? window._Cohesion.sessionId
      : "undefined-session-id";

  if (!existingData?.sessionId) {
    existingData.sessionId = cohesionSessionId;
  }

  return {
    env: "",
    controlNumber: "",
    sessionId: cohesionSessionId, // Swapped uuid() for Cohesion session ID
    shownReturnVisitorOffer: false,
    ...existingData, // Preserve existing values (including timestamp!)
    ...newData, // Allow explicit overrides
  };
};

/**
 * Set the returnVisitor cookie
 * @param {Object} data - Partial cookie data to set/update
 */
export const setReturnVisitorCookie = (data = {}) => {
  if (!hasMarketingConsent()) {
    console.warn(
      "[ReturnVisitor] Cannot set cookie: marketing consent not granted",
    );
    return;
  }

  try {
    const existingCookie = getCookie(COOKIE_NAME);
    const existingData = existingCookie ? JSON.parse(existingCookie) : {};
    const cookieData = buildCookieData(existingData, data);
    const cookieValue = JSON.stringify(cookieData);

    setCookie(COOKIE_NAME, cookieValue, COOKIE_EXPIRES_MINUTES, {
      secure: true,
      sameSite: "Lax",
    });
  } catch (error) {
    console.error(
      "[ReturnVisitor] Failed to set return visitor cookie:",
      error,
    );
  }
};

/**
 * Remove the returnVisitor cookie
 */
export const removeReturnVisitorCookie = () => {
  deleteCookie(COOKIE_NAME);
  console.info("[ReturnVisitor] Cookie removed");
};

/**
 * Validate cookie data structure
 * @param {Object} data - Cookie data to validate
 * @returns {boolean}
 */
const isValidCookieStructure = (data) => Boolean(data?.sessionId);

/**
 * Get the returnVisitor cookie
 * @returns {Object|null} - The cookie data or null if not found/invalid
 */
export const getReturnVisitorCookie = () => {
  if (!hasMarketingConsent()) {
    console.warn(
      "[ReturnVisitor] Cannot read cookie: marketing consent not granted",
    );
    return null;
  }

  try {
    const cookieValue = getCookie(COOKIE_NAME);
    if (!cookieValue) return null;

    const parsed = JSON.parse(cookieValue);

    if (!isValidCookieStructure(parsed)) {
      console.warn(
        "[ReturnVisitor] Invalid cookie structure, removing:",
        parsed,
      );
      removeReturnVisitorCookie();
      return null;
    }

    console.info("[ReturnVisitor] Cookie retrieved:", parsed);
    return parsed;
  } catch (error) {
    console.error(
      "[ReturnVisitor] Failed to parse return visitor cookie:",
      error,
    );
    removeReturnVisitorCookie();
    return null;
  }
};

/**
 * Check if a return visitor cookie exists and is valid
 * @returns {boolean}
 */
export const hasValidReturnVisitorCookie = () => {
  const cookie = getReturnVisitorCookie();
  return cookie !== null;
};
